import { useState } from 'react';
import { ExternalLink, Play, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PROVIDER_LABELS, type ExternalVideo } from './types';
import { parseVideoUrl, videoEmbedUrl } from './videoUrl';

export function ExternalVideoPlayer({
  video,
  fallbackCover,
}: {
  video: ExternalVideo;
  fallbackCover?: string | null;
}) {
  const { t } = useTranslation('commerce');
  const [activated, setActivated] = useState(false);
  const [failed, setFailed] = useState(false);
  const embed = videoEmbedUrl(video);
  const safeLink = parseVideoUrl(video.url)?.url;
  const cover = video.cover_url || fallbackCover;
  const provider = PROVIDER_LABELS[video.provider];
  const ratio =
    video.aspect_ratio === 'portrait'
      ? 'aspect-[9/16] max-w-sm'
      : video.aspect_ratio === 'square'
        ? 'aspect-square max-w-xl'
        : 'aspect-video';
  return (
    <figure className="w-full min-w-0 space-y-2" data-external-video={video.provider}>
      <div
        className={`relative mx-auto w-full overflow-hidden rounded-xl border border-[var(--store-border,theme(colors.slate.200))] bg-[var(--store-surface-low,theme(colors.slate.100))] dark:border-[var(--store-border,theme(colors.charcoal.700))] dark:bg-[var(--store-surface-low,theme(colors.charcoal.900))] ${ratio}`}
      >
        {activated && embed && !failed ? (
          <iframe
            key={embed}
            src={embed}
            title={video.title}
            className="absolute inset-0 h-full w-full border-0"
            allow="fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            onError={() => setFailed(true)}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setFailed(false);
              setActivated(true);
            }}
            disabled={!embed}
            className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-3 p-4 text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--store-accent,theme(colors.lime.600))] dark:text-white"
            aria-label={t('media.play', { title: video.title })}
          >
            {cover && (
              <img
                src={cover}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-contain"
              />
            )}
            <span
              style={{
                backgroundColor: 'var(--store-accent, var(--color-charcoal-950))',
                color: 'var(--store-accent-foreground, var(--color-white))',
              }}
              className="relative flex size-14 items-center justify-center rounded-full bg-charcoal-950 text-white shadow-sm"
            >
              <Play className="size-6" aria-hidden="true" />
            </span>
            {!cover && (
              <span className="relative text-center text-sm font-semibold">{video.title}</span>
            )}
          </button>
        )}
      </div>
      <figcaption className="flex flex-wrap items-center justify-between gap-2 text-sm text-[var(--store-ink-muted,theme(colors.slate.600))] dark:text-[var(--store-ink-muted,theme(colors.slate.300))]">
        <span className="font-medium">{video.title}</span>
        <span className="flex flex-wrap items-center gap-3">
          {activated && (
            <button
              type="button"
              onClick={() => setActivated(false)}
              className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 focus-visible:ring-2 focus-visible:ring-[var(--store-accent,theme(colors.lime.600))]"
            >
              <X className="size-4" />
              {t('media.close_player')}
            </button>
          )}
          {safeLink && (
            <a
              href={safeLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-[var(--store-accent,theme(colors.lime.600))]"
            >
              {t('media.view_on', { provider })}
              <ExternalLink className="size-4" aria-hidden="true" />
            </a>
          )}
        </span>
      </figcaption>
      {(failed || activated) && (
        <p className="text-xs text-slate-600 dark:text-slate-300">{t('media.playback_help')}</p>
      )}
    </figure>
  );
}
